import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

const STATUT_COLORS = { payee: '#2e7d32', en_attente: '#f57c00', en_retard: '#e53935' };

export default function EncaissementsPage() {
  const [, setEncaissements] = useState([]);
  const [factures, setFactures] = useState([]);
  const [stats, setStats] = useState({ encaisse_mois: 0, en_attente: 0, en_retard: 0, taux_recouvrement: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ facture_id: '', montant: '', mode_paiement: 'Especes', reference_paiement: '', notes: '' });
  const [filterStatut, setFilterStatut] = useState('');

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try {
      const [e, s, f] = await Promise.all([
        api.get('/comptabilite/encaissements'),
        api.get('/comptabilite/encaissements/stats'),
        api.get('/factures').catch(() => ({ data: [] })),
      ]);
      setEncaissements(e.data);
      setStats(s.data);
      setFactures(f.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/comptabilite/encaissements', form);
      setShowForm(false);
      load();
    } catch {}
  };

  const marquerPayee = async (factureId, montant) => {
    try {
      await api.post('/comptabilite/encaissements', { facture_id: factureId, montant, mode_paiement: 'Especes' });
      load();
    } catch {}
  };

  const facturesFiltered = factures.filter(f => {
    if (!filterStatut) return true;
    if (filterStatut === 'en_retard') return f.statut === 'en_attente' && f.date_echeance && new Date(f.date_echeance) < new Date();
    return f.statut === filterStatut;
  });

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Encaisse ce mois</span></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{Number(stats.encaisse_mois).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">En attente</span></div>
          <div className="stat-card-value" style={{ color: '#f57c00' }}>{Number(stats.en_attente).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">En retard</span></div>
          <div className="stat-card-value" style={{ color: '#DC2626' }}>{Number(stats.en_retard).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Taux recouvrement</span></div>
          <div className="stat-card-value">{stats.taux_recouvrement}%</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div className="content-card-title">Factures & Encaissements</div>
            <select className="gss-input" value={filterStatut} onChange={e => setFilterStatut(e.target.value)} style={{ width: 150 }}>
              <option value="">Tous</option>
              <option value="en_attente">En attente</option>
              <option value="en_retard">En retard</option>
              <option value="payee">Payees</option>
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={() => setShowForm(true)}>+ Enregistrer paiement</button>
        </div>

        {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>N° Facture</th><th>Client</th><th>Montant</th><th>Date emission</th><th>Echeance</th><th>Statut</th><th>Actions</th></tr></thead>
              <tbody>
                {facturesFiltered.map(f => {
                  const enRetard = f.statut === 'en_attente' && f.date_echeance && new Date(f.date_echeance) < new Date();
                  const statut = enRetard ? 'en_retard' : (f.statut || 'en_attente');
                  const sc = STATUT_COLORS[statut] || '#999';
                  return (
                    <tr key={f.id} style={enRetard ? { background: '#FEF2F2' } : undefined}>
                      <td style={{ fontWeight: 600 }}>{f.numero}</td>
                      <td>{f.client_nom || '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(f.montant_ttc).toLocaleString()} F</td>
                      <td>{f.date_facture?.split('T')[0]}</td>
                      <td>{f.date_echeance?.split('T')[0] || '-'}</td>
                      <td><span className="gss-badge" style={{ background: sc + '20', color: sc }}>{statut === 'en_retard' ? 'En retard' : statut === 'payee' ? 'Payee' : 'En attente'}</span></td>
                      <td>
                        {f.statut !== 'payee' && (
                          <button className="gss-btn-sm" style={{ color: '#2e7d32' }} onClick={() => marquerPayee(f.id, f.montant_ttc)}>Marquer payee</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Enregistrer un paiement</h2>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Facture</label>
                    <select className="gss-input" value={form.facture_id} onChange={e => { const f = factures.find(x => x.id === e.target.value); setForm(prev => ({ ...prev, facture_id: e.target.value, montant: f ? f.montant_ttc : prev.montant })); }}>
                      <option value="">-- Optionnel --</option>
                      {factures.filter(f => f.statut !== 'payee').map(f => <option key={f.id} value={f.id}>{f.numero} — {Number(f.montant_ttc).toLocaleString()} F</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Montant *</label><input className="gss-input" type="number" required min="0" value={form.montant} onChange={e => setForm({ ...form, montant: e.target.value })} /></div>
                  <div className="gss-field"><label>Mode paiement</label>
                    <select className="gss-input" value={form.mode_paiement} onChange={e => setForm({ ...form, mode_paiement: e.target.value })}>
                      <option value="Especes">Especes</option><option value="Virement">Virement</option><option value="Cheque">Cheque</option><option value="Mobile Money">Mobile Money</option>
                    </select>
                  </div>
                  <div className="gss-field"><label>Reference</label><input className="gss-input" value={form.reference_paiement} onChange={e => setForm({ ...form, reference_paiement: e.target.value })} /></div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
